/**
 * LocalStorage abstraction layer for better testability
 * @description LocalStorageへのアクセスを抽象化し、テスト時のモック化を容易にする
 */

export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

/**
 * Browser LocalStorage adapter
 */
class BrowserStorageAdapter implements StorageAdapter {
  getItem(key: string): string | null {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  setItem(key: string, value: string): void {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(key, value);
    } catch {
      // Silently fail in case of quota exceeded
    }
  }

  removeItem(key: string): void {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Silently fail
    }
  }

  clear(): void {
    if (typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.clear();
    } catch {
      // Silently fail
    }
  }
}

/**
 * In-memory storage adapter for testing
 */
export class MemoryStorageAdapter implements StorageAdapter {
  private storage = new Map<string, string>();

  getItem(key: string): string | null {
    return this.storage.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value);
  }

  removeItem(key: string): void {
    this.storage.delete(key);
  }

  clear(): void {
    this.storage.clear();
  }
}

/**
 * Storage manager with configurable adapter
 */
export class StorageManager {
  private static adapter: StorageAdapter = new BrowserStorageAdapter();

  /**
   * Set the storage adapter (useful for testing)
   */
  static setAdapter(adapter: StorageAdapter): void {
    this.adapter = adapter;
  }

  /**
   * Reset to default browser adapter
   */
  static resetAdapter(): void {
    this.adapter = new BrowserStorageAdapter();
  }

  /**
   * Get item from storage
   */
  static getItem(key: string): string | null {
    try {
      return this.adapter.getItem(key);
    } catch {
      return null;
    }
  }

  /**
   * Set item in storage
   */
  static setItem(key: string, value: string): void {
    try {
      this.adapter.setItem(key, value);
    } catch {
      // Silently fail
    }
  }

  /**
   * Remove item from storage
   */
  static removeItem(key: string): void {
    try {
      this.adapter.removeItem(key);
    } catch {
      // Silently fail
    }
  }

  /**
   * Clear all storage
   */
  static clear(): void {
    try {
      this.adapter.clear();
    } catch {
      // Silently fail
    }
  }

  /**
   * Helper to get and parse JSON
   */
  static getJSON<T>(key: string): T | null {
    const value = this.getItem(key);
    if (!value) return null;

    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  /**
   * Helper to set JSON
   */
  static setJSON<T>(key: string, value: T): void {
    try {
      this.setItem(key, JSON.stringify(value));
    } catch {
      // Silently fail on circular references
    }
  }
}
